import { useQuery } from '@tanstack/react-query';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/context/AuthContext';

export interface CompanyRequest {
  id: string;
  company: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status?: string;
  createdAt: string;
}

// Läs in cache direkt en gång vid start av appen för att slippa delay
const getCachedData = (): CompanyRequest[] => {
  try {
    const cached = localStorage.getItem('cachedCompanyRequests');
    return cached ? JSON.parse(cached) : [];
  } catch (e) {
    return [];
  }
};

const initialData = getCachedData();

export const useCompanyRequests = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['companyRequests', user?.uid],
    queryFn: async () => {
      // Om ingen användare är inloggad än, försök hämta om vi har glömt user men firebase har auth.currentUser
      // (som backup ifall AuthContext inte hunnit uppdateras men hooken körs)
      const currentUser = user || auth.currentUser;
      
      if (!currentUser) return initialData; 

      const token = await currentUser.getIdToken();

      const response = await fetch('https://europe-west1-dagens-l-tt.cloudfunctions.net/getCompanyRequests', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data: CompanyRequest[] = await response.json();
      
      // Spara ner en backup i localStorage för omedelbar visning vid nästa sidladdning (UX)
      try {
        localStorage.setItem('cachedCompanyRequests', JSON.stringify(data));
      } catch(e) {}

      return data;
    },
    placeholderData: initialData,
    staleTime: 0, // Alltid "gammalt", så att den alltid kör en bakgrundsuppdatering vid sidladdning
    gcTime: 1000 * 60 * 60, // 1 timme i RAM
  });
};


